import {
  CalendarOutlined,
  FilterOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { Button, Card, DatePicker, Tag, TreeSelect, Typography } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { KitCampaign } from 'models/KitCampaign';
import { User } from 'models/User';

import { UseDashboardFiltersReturn } from '../hooks/useDashboardFilters';
import { WeekDay } from './WeekDayPicker';
import WeekDayPicker from './WeekDayPicker';

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

const BASE =
  'scenes.kit.dashboard_alternative_mode.components.dashboard_filters';

const MONTHS_ES = [
  'enero',
  'febrero',
  'marzo',
  'abril',
  'mayo',
  'junio',
  'julio',
  'agosto',
  'septiembre',
  'octubre',
  'noviembre',
  'diciembre',
];
const DAYS_ES = [
  'Domingo',
  'Lunes',
  'Martes',
  'Miércoles',
  'Jueves',
  'Viernes',
  'Sábado',
];

interface DashboardFiltersProps {
  filters: UseDashboardFiltersReturn;
  campaigns: KitCampaign[];
  metadataLoading: boolean;
  loading: boolean;
  user: User;
}

const ALL_WEEK_DAYS: WeekDay[] = [1, 2, 3, 4, 5, 6, 7];

function getPresets(t: (key: string) => string) {
  const today = dayjs().startOf('day');
  const yesterday = today.subtract(1, 'day');
  const lastWeekStart = today.subtract(1, 'week').startOf('isoWeek');
  const lastWeekEnd = lastWeekStart.endOf('isoWeek').startOf('day');

  return [
    {
      label: t(`${BASE}.presets.today`),
      range: [today, today] as [Dayjs, Dayjs],
      weekDays: ALL_WEEK_DAYS,
    },
    {
      label: t(`${BASE}.presets.yesterday`),
      range: [yesterday, yesterday] as [Dayjs, Dayjs],
      weekDays: ALL_WEEK_DAYS,
    },
    {
      label: t(`${BASE}.presets.last_week`),
      range: [lastWeekStart, lastWeekEnd] as [Dayjs, Dayjs],
      weekDays: ALL_WEEK_DAYS,
    },
  ];
}

function getGreetingKey(): string {
  const hour = new Date().getHours();
  if (hour >= 6 && hour < 13) return 'morning';
  if (hour >= 13 && hour < 20) return 'afternoon';
  return 'evening';
}

function DashboardFilters({
  filters,
  campaigns,
  metadataLoading,
  loading,
  user,
}: DashboardFiltersProps) {
  const { t, i18n } = useTranslation();
  const dateFormat: string = useSelector((state: any) => state.intl.dateFormat);

  const {
    dateRange,
    setDateRange,
    selectedCampaignIds,
    setSelectedCampaignIds,
    selectedWeekDays,
    setSelectedWeekDays,
    disabledDate,
    onApply,
    onReset,
    applyPreset,
  } = filters;

  const now = new Date();
  const formattedDate =
    i18n.language === 'es'
      ? `${DAYS_ES[now.getDay()]}, ${now.getDate()} de ${
          MONTHS_ES[now.getMonth()]
        } de ${now.getFullYear()}`
      : now.toLocaleDateString('en-US', {
          weekday: 'long',
          day: 'numeric',
          month: 'long',
          year: 'numeric',
        });

  const treeData = Object.values(
    campaigns.reduce(
      (acc, c) => {
        const key = `company-${c.company.id}`;
        if (!acc[key]) {
          acc[key] = { title: c.company.name, value: key, children: [] };
        }
        acc[key].children.push({ title: c.name, value: c.id });
        return acc;
      },
      {} as Record<
        string,
        {
          title: string;
          value: string;
          children: { title: string; value: number }[];
        }
      >
    )
  ).map((node) => ({
    ...node,
    title: `${node.title} (${node.children.length})`,
  }));

  return (
    <div className="kit-dashboard-alt__filters">
      {/* Fila superior: saludo + fecha */}
      <div className="kit-dashboard-alt__filters-greeting">
        <Title level={4} style={{ margin: 0, lineHeight: 1.3 }}>
          {t(`${BASE}.greeting.${getGreetingKey()}`)}, {user.name}
        </Title>
        <Text
          type="secondary"
          style={{
            fontSize: 13,
            display: 'flex',
            alignItems: 'center',
            gap: 5,
          }}
        >
          <CalendarOutlined style={{ fontSize: 13 }} />
          {formattedDate}
        </Text>
      </div>

      {/* Fila inferior: controles en Card blanco */}
      <Card className="kit-dashboard-alt__filters-card" size="small">
        <div className="kit-dashboard-alt__filters-controls">
          {' '}
          {/* Rango de fechas */}
          <div className="kit-dashboard-alt__filter-field kit-dashboard-alt__filter-field--fixed">
            <Text className="kit-dashboard-alt__filter-label">
              {t(`${BASE}.labels.date_range`)}
            </Text>
            <RangePicker
              value={dateRange}
              onChange={(dates) => {
                if (dates?.[0] && dates?.[1]) {
                  setDateRange([dates[0] as Dayjs, dates[1] as Dayjs]);
                }
              }}
              disabledDate={disabledDate}
              disabled={loading}
              allowClear={false}
              format={dateFormat}
              className="kit-dashboard-alt__range-picker"
            />
          </div>
          {/* Campañas — crece dinámicamente */}
          <div className="kit-dashboard-alt__filter-field kit-dashboard-alt__filter-field--grow">
            <Text className="kit-dashboard-alt__filter-label">
              {t(`${BASE}.labels.campaigns`)}
            </Text>
            <TreeSelect
              treeData={treeData}
              value={selectedCampaignIds}
              onChange={(ids) =>
                setSelectedCampaignIds(
                  (ids as (number | string)[]).filter(
                    (v): v is number => typeof v === 'number'
                  )
                )
              }
              treeCheckable
              showCheckedStrategy={TreeSelect.SHOW_CHILD}
              showSearch
              filterTreeNode={(search, node) => {
                const normalize = (str: string) =>
                  str
                    .toLowerCase()
                    .normalize('NFD')
                    .replace(/[\u0300-\u036f]/g, '');
                return normalize(String(node.title ?? '')).includes(
                  normalize(search)
                );
              }}
              allowClear
              disabled={metadataLoading || loading}
              placeholder={t(`${BASE}.placeholders.all_campaigns`)}
              className="kit-dashboard-alt__campaign-select"
              style={{ width: '100%' }}
            />
          </div>
          {/* Días de la semana */}
          <div className="kit-dashboard-alt__filter-field">
            <Text className="kit-dashboard-alt__filter-label">
              {t(`${BASE}.labels.week_days`)}
            </Text>
            <WeekDayPicker
              value={selectedWeekDays}
              onChange={setSelectedWeekDays}
              disabled={loading}
            />
          </div>
          {/* Botones */}
          <div className="kit-dashboard-alt__filter-actions">
            <Button
              type="primary"
              icon={<FilterOutlined />}
              onClick={onApply}
              loading={loading}
              disabled={loading}
            >
              {t(`${BASE}.buttons.apply`)}
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={onReset}
              disabled={loading}
            >
              {t(`${BASE}.buttons.reset`)}
            </Button>
          </div>
        </div>

        {/* Fila de favoritos */}
        <div className="kit-dashboard-alt__filters-presets">
          {getPresets(t).map((preset) => (
            <Tag
              key={preset.label}
              className="kit-dashboard-alt__preset-tag"
              onClick={() =>
                !loading && applyPreset(preset.range, preset.weekDays)
              }
            >
              {preset.label}
            </Tag>
          ))}
        </div>
      </Card>
    </div>
  );
}

export default DashboardFilters;
